import { useCallback, useState, type CSSProperties } from 'react';

/**
 * A thin vertical drag bar for resizing a docked side panel (Altium-style).
 * `edge` is the side of the PANEL the handle sits on: a left-dock panel
 * (Projects) puts its handle on the right, a right-dock panel (Inspector) on
 * the left. Dragging outward grows the panel.
 */
export function ResizeHandle({
  edge,
  width,
  setWidth,
  min = 200,
  max = 560,
}: {
  edge: 'left' | 'right';
  width: number;
  setWidth: (w: number) => void;
  min?: number;
  max?: number;
}) {
  const [active, setActive] = useState(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      setActive(true);
      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        const next = edge === 'right' ? startW + delta : startW - delta;
        setWidth(Math.max(min, Math.min(max, next)));
      };
      const onUp = () => {
        setActive(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [edge, width, setWidth, min, max],
  );

  return (
    <div
      onPointerDown={onPointerDown}
      title="Drag to resize"
      style={{
        ...handleStyle,
        [edge === 'right' ? 'right' : 'left']: -3,
        background: active ? 'var(--accent)' : 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLDivElement).style.background = 'var(--accent-soft)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    />
  );
}

const handleStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 6,
  cursor: 'ew-resize',
  zIndex: 5,
  transition: 'background 90ms',
};

/** Load a persisted panel width from localStorage, clamped. */
export function loadPanelWidth(key: string, fallback: number, min = 200, max = 560): number {
  try {
    const v = localStorage.getItem(key);
    if (v) {
      const f = parseFloat(v);
      if (Number.isFinite(f)) return Math.max(min, Math.min(max, f));
    }
  } catch {
    // ignore
  }
  return fallback;
}
